import { useMemo, useState } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { ArrowRight, CalendarDays, Search } from 'lucide-react'
import { companyInfo, designTokens, overviewFilters } from '../data/siteData'
import { useSiteContent } from '../context/SiteContentContext'
import { ArticleShell, DetailMeta, GalleryGrid, HeroBanner, RichText } from '../components/ContentBlocks'
import { normalizePath, slugifyValue, stripHtml, truncateText } from '../lib/route'
import { ContactForm } from '../components/ContactForm'
import { JumpBookingWidget } from '../components/JumpBookingWidget'
import { getClassCategory } from '../components/SiteChrome'

function Meta({ title, description, canonical, image, noIndex = false }) {
  return (
    <>
      <title>{title}</title>
      {description ? <meta name="description" content={description} /> : null}
      <meta property="og:title" content={title} />
      {description ? <meta property="og:description" content={description} /> : null}
      {image ? <meta property="og:image" content={image} /> : null}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      {image ? <meta name="twitter:image" content={image} /> : null}
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      {noIndex ? <meta name="robots" content="noindex,nofollow" /> : null}
    </>
  )
}

function useRouteParts() {
  const location = useLocation()
  const normalizedPath = normalizePath(location.pathname)
  const segments = normalizedPath.split('/').filter(Boolean)
  return { location, normalizedPath, segments }
}

function buildArchiveItems(entries, pageIndex) {
  return pageIndex === 2 ? entries.slice(3, 6) : entries.slice(0, 6)
}

export function StaticPage() {
  const { normalizedPath, segments } = useRouteParts()
  const { findPage, getImage, boardMembers, latestNews, classes } = useSiteContent()
  const baseSlug = segments[0] || ''
  const page = findPage(normalizedPath.replace(/^\//, '')) || findPage(baseSlug)

  if (!page) {
    return <NotFoundPage />
  }

  if (page.page_slug === 'bestyrelse') {
    return <BoardPage />
  }

  if (page.page_slug === 'galleri') {
    return <GalleryPage />
  }

  if (page.page_slug === 'soeg') {
    return <SearchPage />
  }

  if (page.page_slug === 'gymnastik-26-27' || page.page_slug === 'gymnastik-25-26' || page.page_slug === 'svomning-26-27' || page.page_slug === 'svomning-25-26') {
    return <OverviewPage page={page} />
  }

  if (page.page_slug === 'kontakt') {
    return <ContactPage />
  }

  const metaDescription = page.meta_description || truncateText(stripHtml(page.content || ''), 180)
  const visibleDescription = page.page_slug === 'tilmelding' ? '' : (page.meta_description || '')
  const image = getImage(page)

  if (page.page_slug === 'tilmelding') {
    return (
      <ArticleShell
        title={page.title}
        description={visibleDescription}
        image={image}
        meta={<Meta title={page.meta_title || page.title} description={metaDescription} canonical={window.location.href} image={image} />}
      >
        <RichText html={page.content} />
        <div className="mt-12 overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Holdoversigt og tilmelding</h2>
          <iframe
            frameBorder="0"
            src="https://www.conventus.dk/dataudv/www/holdoversigt_ny_iframe.php?foreningsid=973&afdelingsid=0&handelsbetingelser=1&reservationer=vis&skjul_nyt_medlem=0&skjul_allerede_medlem=0&raekkefoelge=alfabetisk&vis_adresse=0&knap_placering=horisontal&highlight=pris&start=periode;ledige_pladser&info=tid_sted;alder;betaling;tilmelding;tilmeld_aabner_om;ledere;om_holdet&sprog=auto"
            className="w-full min-h-[1000px] border-0"
            title="Conventus Holdoversigt"
          />
        </div>
      </ArticleShell>
    )
  }

  return (
    <ArticleShell
      title={page.title}
      description={visibleDescription}
      image={image}
      meta={<Meta title={page.meta_title || page.title} description={metaDescription} canonical={window.location.href} image={image} />}
    >
      <RichText html={page.content} />
    </ArticleShell>
  )
}

const scheduleData = [
  {
    category: "Børnehold",
    items: [
      {
        name: "Forældre/barn 1-3 år",
        sessions: [
          { time: "Mandag 16.15 – 17.00", location: "Lykkesgård Skole, Gymnastiksalen" },
          { time: "Mandag 16.00 – 16.45", location: "Sct. Jacobi Skole, Multisalen" },
          { time: "Mandag 16.50 – 17.35", location: "Sct. Jacobi Skole, Multisalen" }
        ]
      },
      {
        name: "Puslinge 3-5 år",
        sessions: [
          { time: "Torsdag 17.00 – 17.45", location: "Sct. Jacobi Skole, Multisalen" },
          { time: "Tirsdag 17.00 – 17.45", location: "Lykkesgård Hallen, den lille del" }
        ]
      },
      {
        name: "Tumleholdet 3-5 år",
        note: "et rent drengehold",
        sessions: [
          { time: "Tirsdag 17.00 – 17.45", location: "Sct. Jacobi Skole, Multisalen" }
        ]
      },
      {
        name: "Familieholdet 1-6 år",
        note: "NYT NYT",
        description: "Holdet er for hele familien, hvor børn og voksne er aktive sammen",
        sessions: [
          { time: "Søndag 9.00 – 9.45", location: "Sct. Jacobi Skole, Multisalen" }
        ]
      }
    ]
  },
  {
    category: "Spring – Lykkesgårdshallen",
    items: [
      {
        name: "Minispring (sidste år i bhv – 0. kl.)",
        sessions: [
          { time: "Tirsdag 16.45 – 17.45", location: "Lykkesgårdshallen" }
        ]
      },
      {
        name: "Spring 1. – 2. kl.",
        sessions: [
          { time: "Tirsdag 18.00 – 19.00", location: "Lykkesgårdshallen" }
        ]
      },
      {
        name: "Spring 3. – 5. kl.",
        note: "KUN FOR DRENGE",
        sessions: [
          { time: "Onsdag 16.30 – 18.00", location: "Lykkesgårdshallen" }
        ]
      },
      {
        name: "Spring 3. – 5. kl.",
        note: "KUN FOR PIGER",
        sessions: [
          { time: "Torsdag 17.00 – 18.30", location: "Lykkesgårdshallen" }
        ]
      },
      {
        name: "Spring 6. – 8. kl.",
        sessions: [
          { time: "Torsdag 18.30 – 20.00", location: "Lykkesgårdshallen" }
        ]
      }
    ]
  },
  {
    category: "Rytme – Dans",
    items: [
      {
        name: "MGP Dance (sidste år i bhv – 0. klasse)",
        sessions: [
          { time: "Torsdag 16.30 – 17.15", location: "Sportium" }
        ]
      },
      {
        name: "MGP Dance (sidste år i bhv. + 0. kl.)",
        sessions: [
          { time: "Torsdag 16.00 – 16.45", location: "Lykkesgård Hallen, bagerste del" }
        ]
      },
      {
        name: "MGP Dance 1.-2. kl.",
        sessions: [
          { time: "Onsdag 17.00 – 17.45", location: "Skovbunden, Frello skolen" }
        ]
      },
      {
        name: "HIT FIT forældre/barn",
        sessions: [
          { time: "Onsdag 17.00 – 18.00", location: "Sportium (Varde Fritidscenter)" }
        ]
      },
      {
        name: "Teen Dance 5.-8. kl.",
        sessions: [
          { time: "Tirsdag 18.00 – 19.00", location: "Sportium (Varde Fritidscenter), Lokale 1" }
        ]
      },
      {
        name: "Rytme 15+",
        sessions: [
          { time: "Tirsdag 18.30 – 20.00", location: "Multisalen, Jacobi Skole" }
        ]
      }
    ]
  },
  {
    category: "Gymnastik for børn og unge med særlige behov",
    items: [
      {
        name: "Vidunderbanden, 2 – 7 år",
        sessions: [
          { time: "Onsdag 16.00 – 16.45", location: "Sct. Jacobi Skole, Multisalen" }
        ]
      },
      {
        name: "Solsikkeholdet, 8 – 16 år",
        sessions: [
          { time: "Onsdag 17.00 – 17.45", location: "Sct. Jacobi Skole, Multisalen" }
        ]
      },
      {
        name: "SolsikkeDANS, 7 – 16 år",
        sessions: [
          { time: "Tirsdag 18.00 – 18.45", location: "Sct. Jacobi Skole, Multisalen" }
        ]
      },
      {
        name: "Bevæg dig glad, 18+",
        sessions: [
          { time: "Onsdag 18.00 – 18.45", location: "Sct. Jacobi Skole, Multisalen" }
        ]
      }
    ]
  },
  {
    category: "For de voksne",
    items: [
      {
        name: "VIP Damehold med opvisning",
        sessions: [
          { time: "Tirsdag 19.30 – 21.00", location: "Lykkesgårdshallen" }
        ]
      },
      {
        name: "Rytmedamer 25+",
        sessions: [
          { time: "Torsdag 19.30 – 21.00", location: "Sct. Jacobi Skole Multisalen" }
        ]
      },
      {
        name: "Jump +15 år",
        note: "Se under fanen Jump for træningstider",
        sessions: []
      },
      {
        name: "Dance Fitness",
        sessions: [
          { time: "Onsdag 19.00 – 20.00", location: "Sct. Jacobi Skole Multisalen" }
        ]
      },
      {
        name: "Yoga",
        sessions: [
          { time: "Torsdag 18.00 – 19.00", location: "Sportium (Varde Fritidscenter), Rotunden" }
        ]
      },
      {
        name: "Yin Yoga",
        sessions: [
          { time: "Torsdag 19.00 – 20.00", location: "Sportium (Varde Fritidscenter), Rotunden" }
        ]
      },
      {
        name: "KOL, Diabetes, Hjerte",
        sessions: [
          { time: "Mandag 10.00 – 11.00", location: "Sportium (Varde Fritidscenter), Lokale 1" }
        ]
      },
      {
        name: "Floorball",
        note: "STARTER UGE 36",
        sessions: [
          { time: "Tirsdag 9.30 – 10.30", location: "Sportium (Varde Fritidscenter), hal 1" }
        ]
      },
      {
        name: "Idræt om dagen",
        note: "STARTER UGE 36",
        sessions: [
          { time: "Onsdag 9.15 – 10.15", location: "Sportium (Varde Fritidscenter), hal 1" }
        ]
      },
      {
        name: "Stolegymnastik",
        note: "kaffe 10.00-10.30",
        sessions: [
          { time: "Mandag 9.00 – 10.30", location: "Medborgerhuset" }
        ]
      },
      {
        name: "Gå hold Syd",
        sessions: [
          { time: "Onsdag 9.00 – 10.00", location: "Vrimehuset" }
        ]
      },
      {
        name: "Gå hold Nord",
        sessions: [
          { time: "Mandag 9.00 – 10.00", location: "Sportium, indgangen fra Dalgasvej" }
        ]
      },
      {
        name: "Motion i det fri",
        sessions: [
          { time: "Tirsdag 9.00 – 10.00", location: "Det gamle Varde Sommerland, ved toiletbygningen" },
          { time: "Fredag 9.00 – 10.00", location: "Det gamle Varde Sommerland ved toiletbygning" }
        ]
      },
      {
        name: "Krolf",
        sessions: [
          { time: "Onsdag 13.30 – 16.00", location: "Arnbjerg" }
        ]
      },
      {
        name: "Ladies DanceMix",
        sessions: [
          { time: "Fredag 09.15 – 10.15", location: "Medborgerhuset" }
        ]
      }
    ]
  }
]

function isSwimTypeOrCat(val) {
  const v = (val || '').toLowerCase()
  return v === 'svømning' || v === 'svoemning' || v === 'svomning'
}

export function OverviewPage({ page }) {
  const { classes } = useSiteContent()
  const { segments, normalizedPath } = useRouteParts()
  const pageSlug = page?.page_slug || normalizedPath.replace(/^\//, '')
  const overviewConfig = overviewFilters[pageSlug] || {}
  const isSubpage = segments.length > 1
  const subCategorySlug = isSubpage ? segments[1] : ''

  const sectionName = useMemo(() => {
    if (subCategorySlug === 'bornehold') return 'Børnehold'
    if (subCategorySlug === 'dans-spring') return 'Dans/Spring'
    if (subCategorySlug === 'voksenhold') return 'Voksenhold'
    if (subCategorySlug === 'senior') return 'Senior'
    if (subCategorySlug === 'special') return 'Special'
    return 'Generelt'
  }, [subCategorySlug])

  const filteredClasses = useMemo(() => {
    if (pageSlug === 'gymnastik-26-27' || pageSlug === 'gymnastik-25-26') {
      const gymClasses = classes.filter(c => {
        const typeTerm = c.terms?.['class-type']?.[0] || c.terms?.['type_tag']?.[0]
        const catTerm = c.terms?.['class-category']?.[0] || c.terms?.['category_tag']?.[0]
        const cType = typeTerm || c.type || ''
        const cCat = catTerm || c.category || ''
        return !isSwimTypeOrCat(cType) && !isSwimTypeOrCat(cCat) && c.page_slug !== 'showdance'
      })
      if (!isSubpage || sectionName === 'Generelt') return gymClasses
      return gymClasses.filter(c => getClassCategory(c.name, c.type, c.category, c) === sectionName)
    }
    if (pageSlug === 'svomning-26-27' || pageSlug === 'svomning-25-26') {
      const swimClasses = classes.filter(c => {
        const typeTerm = c.terms?.['class-type']?.[0] || c.terms?.['type_tag']?.[0]
        const catTerm = c.terms?.['class-category']?.[0] || c.terms?.['category_tag']?.[0]
        const cType = typeTerm || c.type || ''
        const cCat = catTerm || c.category || ''
        return isSwimTypeOrCat(cType) || isSwimTypeOrCat(cCat)
      })
      if (!isSubpage || sectionName === 'Generelt') return swimClasses
      return swimClasses.filter(c => getClassCategory(c.name, c.type, c.category, c) === sectionName)
    }
    return classes.filter(c => c.page_slug !== 'showdance')
  }, [classes, pageSlug, sectionName, isSubpage])

  const baseTitle = page?.title || 'Oversigt'
  const displayTitle = isSubpage && sectionName !== 'Generelt' ? `${baseTitle} – ${sectionName}` : baseTitle
  const description = page?.meta_description || truncateText(stripHtml(page?.content || ''), 180)

  const isGymnastikSubpage = (pageSlug === 'gymnastik-26-27' || pageSlug === 'gymnastik-25-26') && isSubpage
  const isGymnastikPage = (pageSlug === 'gymnastik-26-27' || pageSlug === 'gymnastik-25-26')
  const isSvomningPage = (pageSlug === 'svomning-26-27' || pageSlug === 'svomning-25-26')

  const displayedSchedule = useMemo(() => {
    if (!isGymnastikSubpage || sectionName === 'Generelt') return scheduleData
    return scheduleData.filter(cat => {
      if (sectionName === 'Børnehold') return cat.category.includes('Børnehold')
      if (sectionName === 'Dans/Spring') return cat.category.includes('Spring') || cat.category.includes('Rytme')
      if (sectionName === 'Special') return cat.category.includes('særlige behov') || cat.category.includes('Special')
      if (sectionName === 'Voksenhold' || sectionName === 'Senior') return cat.category.includes('voksne')
      return true
    })
  }, [isGymnastikSubpage, sectionName])

  const subtitle = (
    <>
      <span>Træningen starter uge 37</span>
      <br />
      <span>Idræt om dagen og Floorball starter i uge 36.</span>
      <br />
      <span>Husk du altid er velkommen til at prøve holdet to gange inden du melder dig til via hjemmesiden.</span>
      <br />
      <span>Vi tager forbehold for rettelser.</span>
    </>
  )

  return (
    <article className="py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <HeroBanner
          eyebrow={baseTitle}
          title={displayTitle}
          subtitle={subtitle}
          compact={isGymnastikSubpage}
          image={page?.image?.[0] || designTokens.programImage}
          ctas={[
            { label: 'Se tilmelding', href: '/tilmelding', primary: true },
            { label: 'Kontakt os', href: '/kontakt' },
          ]}
        />
        {isSvomningPage && page?.content && (
          <div className="mt-12 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <RichText html={page.content} />
          </div>
        )}
        {isSvomningPage && (
          <div className="mt-12 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8 overflow-hidden">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Tilmelding</h2>
            <iframe
              frameBorder="0"
              src="https://www.conventus.dk/dataudv/www/holdoversigt_ny_iframe.php?foreningsid=973&afdelingsid=0&handelsbetingelser=1&reservationer=vis&skjul_nyt_medlem=0&skjul_allerede_medlem=0&raekkefoelge=alfabetisk&vis_adresse=0&knap_placering=horisontal&highlight=pris&start=periode;ledige_pladser&info=tid_sted;alder;betaling;tilmelding;tilmeld_aabner_om;ledere;om_holdet&sprog=auto"
              className="w-full min-h-[1000px] border-0"
              title="Conventus Holdoversigt Iframe"
            />
          </div>
        )}

        {/* Dynamic Class Cards */}
        {filteredClasses.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-tight text-slate-950">
                {isSubpage && sectionName !== 'Generelt' ? `Hold i ${sectionName}` : 'Holdoversigt'}
              </h2>
              <span className="text-sm font-medium text-slate-500">{filteredClasses.length} hold</span>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredClasses.map((item) => (
                <Link
                  key={item.id || item.page_slug || item.name}
                  to={`/${item.page_slug}`}
                  className="group flex flex-col justify-between overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-[#3B70E5] hover:shadow-xl"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center rounded-full bg-[#3B70E5]/10 px-3 py-1 text-xs font-semibold text-[#3B70E5]">
                        {getClassCategory(item.name, item.type, item.category, item)}
                      </span>
                      {item.age && <span className="text-xs font-medium text-slate-500">{item.age}</span>}
                    </div>
                    <h3 className="mt-4 text-xl font-bold tracking-tight text-slate-950 group-hover:text-[#3B70E5] transition-colors">
                      {item.name}
                    </h3>
                    {item.time && (
                      <p className="mt-2 text-sm font-medium text-slate-700 flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#3B70E5]"></span>
                        {item.time}
                      </p>
                    )}
                    {item.location && <p className="mt-1 text-xs text-slate-500">{item.location}</p>}
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-900 group-hover:text-[#3B70E5] transition-colors">
                      Læs mere om holdet
                    </span>
                    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#3B70E5] group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {isGymnastikPage && (
          <div className="mt-12 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <div className="bg-slate-900 px-6 py-5 text-white sm:px-8">
              <h2 className="text-xl font-bold tracking-tight">Træningstider og lokationer – Gymnastik 2026/2027</h2>
              <p className="mt-1 text-xs text-slate-300 uppercase tracking-wider">Oversigt over alle gymnastikhold</p>
            </div>
            <div className="divide-y divide-slate-200">
              {displayedSchedule.map((cat, catIdx) => (
                <div key={catIdx} className="p-6 sm:p-8">
                  <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3 mb-4 flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#3B70E5]"></span>
                    {cat.category}
                  </h3>
                  <div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
                    <table className="block sm:table w-full text-left border-collapse">
                      <thead className="hidden sm:table-header-group">
                        <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">
                          <th className="py-3 pr-4 w-1/2">Hold / Alder</th>
                          <th className="py-3 pl-4 w-1/2">Træningstid & Sted / Bemærkning</th>
                        </tr>
                      </thead>
                      <tbody className="block sm:table-row-group divide-y divide-slate-100 text-sm">
                        {cat.items.map((item, itemIdx) => (
                          <tr key={itemIdx} className="block sm:table-row py-4 sm:py-0 hover:bg-slate-50/50 transition-colors">
                            <td className="block sm:table-cell py-2 sm:py-4 pr-0 sm:pr-4 font-semibold text-slate-900">
                              <div className="flex flex-col gap-1">
                                <span className="text-base font-bold text-slate-950">{item.name}</span>
                                {item.description && (
                                  <span className="text-xs font-normal text-slate-500 leading-relaxed max-w-xs">
                                    {item.description}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="block sm:table-cell py-2 sm:py-4 pl-0 sm:pl-4 text-slate-700">
                              {item.sessions.length > 0 ? (
                                <div className="space-y-3">
                                  {item.sessions.map((session, sIdx) => (
                                    <div key={sIdx} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                                      <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#3B70E5]"></span>
                                        {session.time}
                                      </div>
                                      <div className="text-sm text-slate-600 sm:border-l sm:border-slate-200 sm:pl-4">
                                        {session.location}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400 italic">Se holdbeskrivelse</span>
                              )}
                              {item.note && (
                                <div className="mt-2">
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tracking-wide ${
                                    item.note.includes("NYT") 
                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
                                      : item.note.includes("KUN") 
                                        ? "bg-blue-50 text-blue-700 border border-blue-200"
                                        : "bg-amber-50 text-amber-700 border border-amber-200"
                                  }`}>
                                    {item.note}
                                  </span>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  )
}

export function ClassPage() {
  const { normalizedPath } = useRouteParts()
  const { classFromPath } = useSiteContent()
  const page = classFromPath(normalizedPath)

  if (!page) return <NotFoundPage />

  const description = page.meta_description || truncateText(stripHtml(page.description || ''), 180)
  const image = page.image?.[0] || ''
  const isJump = page.page_slug === 'jump-fitness' || page.page_slug === 'jump' || page.name?.toLowerCase().includes('jump')

  return (
    <ArticleShell
      title={page.name}
      description={description}
      image={image}
      meta={<Meta title={page.meta_title || `${page.name} - Varde Gymnastikforening`} description={description} canonical={window.location.href} image={image} />}
    >
      <DetailMeta items={[page.age, page.time, page.location, page.price, page.instructor].filter(Boolean)} />
      <RichText html={page.description} />
      {isJump && <JumpBookingWidget />}
      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/tilmelding" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3B70E5] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#3B70E5]/90">
          Tilmelding <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-[#3B70E5] hover:bg-[#3B70E5]/10">
          Kontakt <ArrowRight className="h-4 w-4" />
        </Link>
        {isJump && (
          <button
            type="button"
            onClick={() => window.open('https://www.conventus.dk/dataudv/www/online_booking.php?forening=973&wizard=dbl&dato=single&bookinger_tid=vaelg_fri_tid&bookinger_opdel=flad&bookinger_kolonner=ressource;pris;antal_pl')}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-[#3B70E5] hover:bg-[#3B70E5]/10 cursor-pointer"
          >
            Book <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </ArticleShell>
  )
}

export function NewsArchivePage({ pageIndex = 1 }) {
  const { latestNews } = useSiteContent()
  const items = buildArchiveItems(latestNews, pageIndex)

  return (
    <article className="py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <HeroBanner
          eyebrow="Nyheder"
          title={pageIndex === 2 ? 'Nyheder - side 2' : 'Nyheder'}
          subtitle="Hold dig opdateret med foreningsnyheder, arrangementer og sæsonstart."
          image={designTokens.splitImage}
          ctas={[{ label: 'Tilmelding', href: '/tilmelding', primary: true }, { label: 'Kontakt', href: '/kontakt' }]}
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {items.map((item) => (
            <Link key={item.news_slug} to={`/${item.news_slug}`} className="group overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
              {item.image?.[0] ? (
                <div className="aspect-[4/3] overflow-hidden bg-slate-200">
                  <img src={item.image[0]} alt={item.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                </div>
              ) : null}
              <div className="p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">{item.date || 'Nyhed'}</p>
                <h3 className="mt-3 text-xl font-bold tracking-tight text-slate-950">{item.title}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">{item.meta_description}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition group-hover:text-[#3B70E5]">
                  Læs mere <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </article>
  )
}

export function NewsDetailPage({ item }) {
  const image = item?.image?.[0] || ''
  const description = item?.meta_description || truncateText(stripHtml(item?.content || ''), 180)

  return (
    <ArticleShell
      title={item.title}
      description={description}
      image={image}
      imagePosition={item.news_slug === 'aarets-traener' ? 'top' : '50% 27%'}
      meta={<Meta title={item.meta_title || item.title} description={description} canonical={window.location.href} image={image} />}
    >
      <div className="mb-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">
        <CalendarDays className="h-4 w-4" />
        {item.date || 'Nyhed'}
      </div>
      <RichText html={item.content} />
    </ArticleShell>
  )
}

export function BoardPage() {
  const { findPage, boardMembers } = useSiteContent()
  const page = findPage('bestyrelse')
  const description = page?.meta_description || 'Kontakt og bestyrelsesinformation'

  return (
    <article className="py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <HeroBanner
          eyebrow="Bestyrelse"
          title={page?.title || 'Bestyrelse'}
          subtitle={description}
          image={page?.image?.[0] || designTokens.homeSupportImage}
          ctas={[{ label: 'Kontakt os', href: '/kontakt', primary: true }, { label: 'Tilmelding', href: '/tilmelding' }]}
        />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {boardMembers.map((member) => (
            <div key={member.id} className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
              {member.image?.[0] ? <img src={member.image[0]} alt={member.name} className="aspect-[4/5] w-full object-cover" /> : null}
              <div className="p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">{member.role}</p>
                <h3 className="mt-3 text-xl font-bold tracking-tight text-slate-950">{member.name}</h3>
                {member.phone ? <p className="mt-2 whitespace-nowrap text-sm text-slate-600">{member.phone}</p> : null}
                {member.email ? <a className="mt-2 block whitespace-nowrap text-sm font-medium text-slate-900 underline underline-offset-4" href={`mailto:${member.email}`}>{member.email}</a> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </article>
  )
}

export function GalleryPage() {
  const { findPage } = useSiteContent()
  const page = findPage('galleri')
  const images = page?.image?.length ? page.image : designTokens.galleryImages

  return (
    <article className="py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <HeroBanner
          eyebrow="Galleri"
          title={page?.title || 'Galleri'}
          subtitle={page?.meta_description || 'Billeder fra foreningslivet og de aktive hold.'}
          image={images[0] || designTokens.heroImage}
          ctas={[{ label: 'Tilmelding', href: '/tilmelding', primary: true }, { label: 'Kontakt', href: '/kontakt' }]}
        />
        <div className="mt-10">
          <GalleryGrid images={images} />
        </div>
      </div>
    </article>
  )
}

export function ContactPage() {
  return (
    <article className="py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <HeroBanner
          eyebrow="Kontakt"
          title="Kontakt Varde Gymnastikforening"
          subtitle="Skriv, ring eller find os på Facebook. Vi svarer så hurtigt vi kan."
          image={designTokens.homeSupportImage}
          ctas={[{ label: 'Tilmelding', href: '/tilmelding', primary: true }, { label: 'Facebook', href: companyInfo.facebook }]}
        />
        <div className="mt-10 grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="grid gap-4 h-fit">
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm h-fit">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">Adresse</p>
              <p className="mt-3 text-sm leading-7 text-slate-700">{companyInfo.address}<br />{companyInfo.zipCity}</p>
            </div>
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm h-fit">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">Telefon</p>
              <p className="mt-3 whitespace-nowrap text-sm font-semibold text-slate-950">{companyInfo.phone}</p>
            </div>
            <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm h-fit">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">Email</p>
              <p className="mt-3 whitespace-nowrap text-sm font-semibold text-slate-950">{companyInfo.email}</p>
            </div>
          </div>
          <ContactForm />
        </div>

        {/* Google Maps Section */}
        <div className="mt-10 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="bg-slate-900 px-6 py-5 text-white sm:px-8">
            <h2 className="text-xl font-bold tracking-tight">Find os her</h2>
            <p className="mt-1 text-xs text-slate-300 uppercase tracking-wider">Varde Gymnastikforening på kortet</p>
          </div>
          <div className="aspect-[16/9] w-full min-h-[350px] sm:min-h-[450px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2460.9444004478096!2d8.486028000000001!3d55.624843!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464b1e66899e6709%3A0x8f74374095720fe!2sVarde%20Gymnastikforening!5e1!3m2!1sen!2sdk!4v1780051209736!5m2!1sen!2sdk"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Varde Gymnastikforening på kort"
              className="w-full h-full"
            />
          </div>
        </div>
      </div>
    </article>
  )
}

export function SearchPage() {
  const { pages, classes, latestNews, getPageSummary } = useSiteContent()
  const [query, setQuery] = useState('')

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    const pageResults = pages
      .filter((item) => (item.title || '').toLowerCase().includes(q) || getPageSummary(item).toLowerCase().includes(q))
      .map((item) => ({ type: 'Side', title: item.title, href: `/${item.page_slug}`, text: getPageSummary(item) }))
    const classResults = classes
      .filter((item) => (item.name || '').toLowerCase().includes(q) || stripHtml(item.description || '').toLowerCase().includes(q))
      .map((item) => ({ type: 'Hold', title: item.name, href: `/${item.page_slug}`, text: item.meta_description || item.age || '' }))
    const newsResults = latestNews
      .filter((item) => (item.title || '').toLowerCase().includes(q) || (item.meta_description || '').toLowerCase().includes(q))
      .map((item) => ({ type: 'Nyhed', title: item.title, href: `/${item.news_slug}`, text: item.meta_description || '' }))
    return [...pageResults, ...classResults, ...newsResults].slice(0, 12)
  }, [classes, getPageSummary, latestNews, pages, query])

  return (
    <article className="py-12 sm:py-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <HeroBanner
          eyebrow="Søg"
          title="Søg efter sider, hold og nyheder"
          subtitle="Brug søgning til hurtigt at finde indhold på tværs af foreningen."
          image={designTokens.splitImage}
          ctas={[{ label: 'Kontakt', href: '/kontakt', primary: true }, { label: 'Tilmelding', href: '/tilmelding' }]}
        />
        <div className="mt-10 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <label htmlFor="search" className="text-sm font-medium text-slate-900">
            Søg
          </label>
          <div className="mt-2 flex items-center gap-3 rounded-2xl border border-slate-300 bg-white px-4 py-3 focus-within:border-[#3B70E5] focus-within:ring-4 focus-within:ring-[#3B70E5]/10">
            <Search className="h-5 w-5 text-slate-400" />
            <input id="search" value={query} onChange={(event) => setQuery(event.target.value)} className="w-full bg-transparent text-slate-900 outline-none placeholder:text-slate-400" placeholder="Søg efter hold, nyheder eller sider" />
          </div>
          {query ? (
            <div className="mt-8 grid gap-4">
              {results.length ? results.map((item) => (
                <Link key={`${item.type}-${item.title}`} to={item.href} className="rounded-2xl border border-slate-200 bg-stone-50 p-4 transition hover:border-[#3B70E5] hover:bg-[#3B70E5]/10">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">{item.type}</p>
                  <h3 className="mt-2 text-lg font-bold text-slate-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{item.text}</p>
                </Link>
              )) : <p className="text-sm text-slate-600">Ingen resultater.</p>}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  )
}

export function NotFoundPage() {
  return (
    <article className="py-12 sm:py-16">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
        <HeroBanner
          eyebrow="404"
          title="Siden blev ikke fundet"
          subtitle="Prøv en af de populære sider herunder."
          image={designTokens.heroImage}
          ctas={[{ label: 'Forside', href: '/', primary: true }, { label: 'Hold', href: '/gymnastik-25-26' }]}
        />
      </div>
    </article>
  )
}
